import { type MouseEvent, useState } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { type OverviewSavedView } from "@features/overview-v2/types";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { DeleteSavedViews } from "./delete-saved-views";
import { UpdateSavedViews } from "./update-saved-views";

const mayOpenNewTab = (value: number | null) => (event: MouseEvent) => {
  event.persist();

  if ((event.ctrlKey || event.metaKey || event.type === "auxclick") && value) {
    event.stopPropagation();

    const savedViewsUrl = new URL(window.location.href);
    savedViewsUrl.searchParams.set("savedViewId", value.toString());
    window.open(savedViewsUrl.href, "_blank");
    return true;
  }

  return false;
};

export interface SavedViewsMenuRowProps {
  savedView: OverviewSavedView;
  className?: string;
  isSelected?: boolean;
  isReordering?: boolean;
  isDefault?: boolean;
  onClick?(): void;
}

export const SavedViewsMenuRow = ({
  savedView,
  isSelected = false,
  isReordering = false,
  isDefault = false,
  onClick,
  ...props
}: SavedViewsMenuRowProps) => {
  const { isDemoMode } = usePrefs();
  const [showDeleteView, setShowDeleteView] = useState(false);
  const [showUpdateView, setShowUpdateView] = useState(false);
  const prettyLogEventPage = usePrettyLogEventPage();

  const { selectSavedView } = useOverviewContext();
  const isTemplateView = savedView.is_template;

  const isSelectable =
    !showDeleteView && !showUpdateView && !isReordering && !isSelected;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: savedView.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const reorderProps = isReordering
    ? { ref: setNodeRef, style, ...attributes, ...listeners }
    : {};

  return (
    <div
      {...props}
      onClick={() => {
        if (isSelectable) {
          prettyLogEventPage({
            domain: "Saved Views",
            action: "onClick",
            additionalActionInfo: "Select Saved View",
            data: {
              pageContext: { savedViewId: savedView.id },
            },
          });
          selectSavedView(savedView, {
            eventType: "select",
            container: "saved-views-menu",
          });
          onClick?.();
        }
      }}
      onAuxClick={(event: MouseEvent) => mayOpenNewTab(savedView.id)(event)}
      className={twMerge(
        props.className,
        " w-full h-10 px-4 flex group items-center bg-v2-primary",
        clsx({
          "cursor-auto": showDeleteView || showUpdateView,
          "cursor-pointer hover:bg-cosmic-4": isSelectable,
          "font-bold bg-cosmic-4": isSelected && !isReordering,
          "bg-mars-2": showDeleteView,
          "border-t": isReordering,
          "border-none": !isReordering,

          "border-v2-secondary": true,
        }),
      )}
    >
      {showUpdateView && (
        <UpdateSavedViews
          savedView={savedView}
          onClose={() => setShowUpdateView(false)}
        />
      )}
      {showDeleteView && (
        <DeleteSavedViews
          savedView={savedView}
          onClose={() => setShowDeleteView(false)}
        />
      )}
      {!showDeleteView && !showUpdateView && (
        <div
          className="flex items-center justify-between w-full truncate"
          {...reorderProps}
        >
          <div className="truncate flex items-center">
            {isReordering && (
              <i className="fa-regular fa-sharp fa-grip-dots text-primary cursor-pointer mr-2" />
            )}
            <div className="truncate">
              <span className="truncate flex-1 w-full">
                {generateAnonBase64Hash({
                  value: savedView.name,
                  prefix: "quick-filter",
                  doAnon: isDemoMode && !savedView.is_template,
                })}
              </span>
              <div className="flex gap-1.5">
                {isDefault && (
                  <div className="text-xxs text-cosmic font-light">Default</div>
                )}
                {isDefault && isTemplateView && (
                  <div className="text-xxs text-cosmic">&bull;</div>
                )}
                {isTemplateView && (
                  <div className="text-xxs text-cosmic font-light">
                    Template
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={clsx(isReordering && "hidden", "flex items-center")}>
            <GenericTooltip
              className={clsx("hidden", {
                "group-hover:block":
                  !isSelected && !isTemplateView && !savedView.is_default,
              })}
              noInfoCircle
              content="Edit"
              mouseEnterDelay={0.5}
            >
              <button
                className="w-6 h-6 rounded-full text-primary ml-2 hover:bg-v2-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUpdateView(true);
                }}
              >
                <i className="fa-regular fa-sharp fa-pen text-xs" />
              </button>
            </GenericTooltip>
            <GenericTooltip
              className={clsx("hidden", { "group-hover:block": !isSelected })}
              noInfoCircle
              content="Delete"
              mouseEnterDelay={0.5}
            >
              <button
                className="w-6 h-6 rounded-full text-mars+2 ml-0.5 hover:bg-v2-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteView(true);
                }}
              >
                <i className="fa-regular fa-sharp fa-trash text-xs" />
              </button>
            </GenericTooltip>
          </div>
        </div>
      )}
    </div>
  );
};
